import { Services, Helpers, AirportsTypes } from '@smiles/core-airlines';
import { getFlightResultURLPath } from '../services';
import {
  IGenerateSearchURL,
  ICustomAutoCompleteSearch,
  flightTypesList,
  IFlightPassagers,
  IIsValidMobileSearch,
  IGetAirportFormattedValue,
  SearchAirportProps,
  IDirection,
  cabinTypesList,
  DataLayerInfo,
} from './types';
import { allAirports } from './constants';

const TITLE_NAME = 'Explorar o mundo';

export function formatAirportsList(
  airports: AirportsTypes.AirportData[]
): ICustomAutoCompleteSearch[] {
  const getIcons = (item: AirportsTypes.AirportData) => {
    if (item.name === TITLE_NAME) {
      return 'travel_explore';
    }
    if (Number(item.latitude) !== 0 && item.name !== allAirports) {
      return 'flight';
    }
    return 'place';
  };

  const getSubtitle = (item: AirportsTypes.AirportData) => {
    if (item.name === TITLE_NAME) {
      return '';
    }
    return `${item.city}, ${item.country}`;
  };

  return airports.map((item) => ({
    ...item,
    iconName: getIcons(item),
    title: item.name,
    subtitle: getSubtitle(item),
    cornerText: item.code,
  }));
}

/* istanbul ignore next */
export const searchAirport = async ({
  filter,
  location,
}: SearchAirportProps): Promise<ICustomAutoCompleteSearch[]> => {
  try {
    const params =
      location?.latitude && location.longitude
        ? {
            filter,
            location,
            size: 50,
          }
        : { filter, size: 50 };
    const { success, data: airportSearchResult } = await Services.findAirports(
      params
    );

    if (!success || !airportSearchResult) {
      return [];
    }

    const isMultiCityFlight = document.querySelector(
      '#drop_fligthType'
    ) as HTMLButtonElement;
    const flightType = isMultiCityFlight
      ? isMultiCityFlight?.childNodes[1]?.textContent
      : '';
    if (filter?.itemType === 'destination' && flightType !== '2 destinos') {
      const airports: AirportsTypes.AirportData[] = [];

      airportSearchResult.map((item) => airports.push(item));

      airports.push({
        name: TITLE_NAME,
        city: '',
        code: '',
        country: '',
      } as AirportsTypes.AirportData);
      return formatAirportsList(airports);
    }
    return formatAirportsList(airportSearchResult);
  } catch (error) {
    console.error(error);
    return [];
  }
};

export function generateSearchUrl(params: IGenerateSearchURL): string | null {
  const {
    origins,
    destinations,
    flightClass,
    flightDates,
    flightType,
    isMultiCityFlight,
    flightPassagers,
  } = params;

  if (!flightDates.firstFlight) {
    return null;
  }

  const flightTypeIndex = Object.keys(flightTypesList).findIndex(
    (key) => key === flightType
  );

  const legs = [
    {
      direction: 'origin' as IDirection,
      airport: String(origins.firstLeg?.code),
      isAirportAny: origins.firstLeg?.name === allAirports,
    },
    {
      direction: 'destination' as IDirection,
      airport: String(destinations.firstLeg?.code),
      isAirportAny: destinations.firstLeg?.name === allAirports,
    },
  ];

  const multiCityFlight = [
    ...legs,
    {
      direction: 'origin' as IDirection,
      airport: String(origins.secondLeg?.code),
    },
    {
      direction: 'destination' as IDirection,
      airport: String(destinations.secondLeg?.code),
    },
  ];

  const getTimestampFromDate = (date: Date): string => {
    return new Date(`${date}`).getTime().toString();
  };

  const returnDate =
    flightType === flightTypesList.ROUND_TRIP &&
    flightDates.firstFlight.returnDate
      ? getTimestampFromDate(flightDates.firstFlight.returnDate)
      : '';

  const airports = [origins.firstLeg, destinations.firstLeg];
  if (isMultiCityFlight) {
    airports.push(origins.secondLeg, destinations.secondLeg);
  }

  const searchType = Helpers.getTripSearchType(airports);
  const urlBasePath = getFlightResultURLPath();

  return Helpers.createSearchFlightsUrl({
    searchType: searchType === 'g3AndCongenere' ? 'both' : searchType,
    tripType: Number(flightTypeIndex + 1).toString(),
    passagers: {
      adults: flightPassagers.adults.toString(),
      children: flightPassagers.children.toString(),
      baby: flightPassagers.babies.toString(),
    },
    tripDate: {
      departure: {
        day1: getTimestampFromDate(flightDates.firstFlight.departureDate),
        day2:
          flightType === flightTypesList.MULTI_CITY &&
          flightDates.secondFlightDate
            ? getTimestampFromDate(flightDates.secondFlightDate)
            : '',
      },
      returnDate,
    },
    pathURL: `${urlBasePath.substring(1)}/`,
    legs: isMultiCityFlight ? multiCityFlight : legs,
    cabin: flightClass.includes('ECONOMY')
      ? cabinTypesList.ECONOMIC
      : flightClass,
  });
}

export function handleChangeCounter(
  counterValues: { value: number; name: string }[]
): IFlightPassagers {
  const [adults, children, babies] = counterValues;
  return {
    adults: adults.value,
    children: children.value,
    babies: babies.value,
  };
}

export function getAirportFormattedValue({
  flight,
  isFirstLeg,
}: IGetAirportFormattedValue): string {
  if (!flight) {
    return '';
  }

  const airport = isFirstLeg ? flight.firstLeg : flight.secondLeg;

  if (!airport) {
    return '';
  }

  /* istanbul ignore next */
  if (!airport.code) {
    return TITLE_NAME;
  }

  return `${airport.city} (${airport.code})`;
}

export function isValidMobileSearch({
  origins,
  destinations,
  dates,
  isMultiCityFlight,
}: IIsValidMobileSearch): boolean {
  if (!origins || !destinations || !dates) {
    return false;
  }
  if (!dates.firstFlight || (isMultiCityFlight && !dates.secondFlightDate)) {
    return false;
  }

  if (!origins.firstLeg) {
    return false;
  }

  if (
    isMultiCityFlight &&
    (!origins.firstLeg ||
      !origins.secondLeg ||
      !destinations.firstLeg ||
      !destinations.secondLeg)
  ) {
    return false;
  }

  return true;
}

export function formatEventToDataLayer(dataLayerInfo: DataLayerInfo) {
  let direction = '';
  if (dataLayerInfo.title === 'Explorar o mundo') {
    direction = dataLayerInfo.title;
  } else {
    direction =
      dataLayerInfo.departureCountry === dataLayerInfo.returnCountry
        ? 'Nacional'
        : 'Internacional';
  }

  let searchPeriod = '';
  if (
    dataLayerInfo.flightType === flightTypesList.ONE_WAY &&
    dataLayerInfo.departureDate
  ) {
    searchPeriod = dataLayerInfo.departureDate
      .toLocaleDateString('pt-BR')
      .replace(/\//g, '-');
  }
  if (dataLayerInfo.departureDate && dataLayerInfo.returnDate) {
    searchPeriod = `${dataLayerInfo.departureDate
      .toLocaleDateString('pt-BR')
      .replace(/\//g, '-')} | ${dataLayerInfo.returnDate
      .toLocaleDateString('pt-BR')
      .replace(/\//g, '-')}`;
  }

  return {
    searchTerm: `${direction} - ${
      dataLayerInfo.flightType === flightTypesList.ROUND_TRIP
        ? 'Ida e volta'
        : 'Somente ida'
    }`,
    passengers: `Adultos=${dataLayerInfo.flightPassengers.adults} | Criancas=${dataLayerInfo.flightPassengers.children} | Bebe=${dataLayerInfo.flightPassengers.babies}`,
    searchPeriod,
  };
}
